import React, { useState, useEffect } from 'react';
import { Search, AlertTriangle, User, Calendar, Stethoscope, MapPinned, Pill, UserCheck, History, CreditCard, DollarSign, FileText, MapPin, Download, ExternalLink, Wallet } from 'lucide-react';
import { members } from '../../data/mockData';
import { memberApi } from '../../api/memberApi';
import { isApiConfigured } from '../../api/axiosClient';
import { LoadingSpinner, ErrorCard } from '../../components/LoadingSpinner';
import { useToast } from '../../components/Toast';
import { userApi } from '../../api/userApi';
//import { formatDate } from '../../components/utils/formatDate';

/**
 * PCP directory — maps a member's PCP name to a clinic address and practice.
 * In production this would come from a provider-network API; for the demo we
 * keep it inline so the eligibility result page can render a complete PCP
 * card without touching every member object in mockData.
 */
const PCP_DIRECTORY = {
    'Dr. Alan Cooper': {
        practice: 'Austin Primary Care',
        address: '4502 N Lamar Blvd, Suite 200, Austin, TX 78751',
    },
    'Dr. Nina Patel': {
        practice: 'Cedar Park Medical Group',
        address: '1820 Cedar Park Dr, Cedar Park, TX 78613',
    },
    'Dr. Sam Rivera': {
        practice: 'Round Rock Family Health',
        address: '305 Round Rock Ave, Suite 110, Round Rock, TX 78664',
    },
};

const MemberSearchPage = () => {
    const [searchBy, setSearchBy] = useState('memberId');
    const [memberId, setMemberId] = useState('10499931-01');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [groups, setGroups] = useState([]);
    const [dob, setDob] = useState('');
    const [groupId, setGroupId] = useState('');
    const [result, setResult] = useState(null);
    const [resultStatus, setResultStatus] = useState(null);
    const [searched, setSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const toast = useToast();

    const cleanLabel = (text) =>
        text?.replace(/copay/gi, '').trim();

    useEffect(() => {
        fetchGroups();
    }, []);

    const fetchGroups = async () => {
        const res = await userApi.groups();

        if (res.success) {
            setGroups(res.data.Groups);
        }
    }


    const handleSearch = async () => {
        setSearched(true);
        setError(null);
        if (isApiConfigured()) {

            setLoading(true);
            try {


               // alert(searchBy);

                if (searchBy.trim().toLowerCase() === 'memberid') {
                    if (!memberId.trim()) {
                        toast.warning(`Member ID is required`, { title: 'Member search' });
                        return;
                    }
                }

                if (searchBy.trim().toLowerCase() === 'name') {
                    if (!firstName.trim() || !lastName.trim() || !dob.trim()) {
                        toast.warning(`First name, Last name and DOB  is required`, { title: 'Member search' });
                        return;
                    }
                }
                

                

                const response = await memberApi.searchMembers({
                    type: searchBy,
                    value: memberId,
                    groupId,
                    firstName,
                    lastName,
                    dob,
                });

                //alert(JSON.stringify(response.data));

                setResultStatus(response.success);
                
                setResult(response.data || null);
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to search members. Please try again.');
                setResult(null);
            } finally {
                setLoading(false);
            }
        } else {
        //if (isApiConfigured()) {
        //    setLoading(true);
        //    try {
        //        const response = await memberApi.searchMembers({
        //            type: searchBy,
        //            value: memberId,
        //            firstName,
        //            lastName,
        //            dob,
        //            groupId,
        //        });
        //        setResult(response.data || null);
        //    } catch (err) {
        //        setError(err.response?.data?.message || 'Failed to search members. Please try again.');
        //        setResult(null);
        //    } finally {
        //        setLoading(false);
        //    }
        //} else {
            // Mock data fallback — simulate a short network round-trip so the
            // Search button's "Searching…" state is perceptible.
            setLoading(true);
            await new Promise((r) => setTimeout(r, 350));
            let found = null;

            if (searchBy === 'memberId') {
                if (!memberId.trim()) { setLoading(false); return; }
                found = members.find(
                    (m) => m.memberId.toLowerCase() === memberId.trim().toLowerCase()
                );
                if (found && groupId && found.groupNumber !== groupId) {
                    found = null;
                }
            } else {
                // Search by name + DOB
                let matches = [...members];
                if (firstName.trim()) {
                    matches = matches.filter((m) =>
                        m.firstName.toLowerCase().includes(firstName.trim().toLowerCase())
                    );
                }
                if (lastName.trim()) {
                    matches = matches.filter((m) =>
                        m.lastName.toLowerCase().includes(lastName.trim().toLowerCase())
                    );
                }
                if (dob) {
                    matches = matches.filter((m) => m.dateOfBirth === dob);
                }
                if (groupId) {
                    matches = matches.filter((m) => m.groupNumber === groupId);
                }
                found = matches.length > 0 ? matches[0] : null;
            }

            setResult(found || null);
            setLoading(false);
        }
    };

    const handleClear = () => {
        setMemberId('');
        setFirstName('');
        setLastName('');
        setDob('');
        setGroupId('');
        setResult(null);
        setSearched(false);
        setLoading(false);
        setError(null);
        setResultStatus(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSearch();
    };

    //const formatDate = (dateStr) => {
    //    if (!dateStr) return 'N/A';
    //    const d = new Date(dateStr + 'T00:00:00');
    //    return d.toLocaleDateString('en-US', {
    //        month: '2-digit',
    //        day: '2-digit',
    //        year: 'numeric',
    //    });
    //};

    const formatDate = (value) => {
        if (!value) return 'N/A';

        const date = new Date(value);

        if (isNaN(date.getTime())) return 'N/A';

        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        const yyyy = date.getFullYear();

        return `${mm}/${dd}/${yyyy}`;
    };

    const statusClass = (status) => {
        return status === 'Active' ? 'active' : 'terminated';
    };

    /* ── Section styling helpers ──────────────────────────────────── */
    const sectionHeaderStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        paddingBottom: 14,
        marginBottom: 16,
        borderBottom: '1px solid #E8ECF0',
    };

    const sectionIconStyle = (color) => ({
        width: 32,
        height: 32,
        borderRadius: 10,
        background: `linear-gradient(135deg, ${color}, ${color}CC)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow: `0 2px 8px ${color}33`,
    });

    const benefitTileStyle = {
        padding: '14px 16px',
        borderRadius: 12,
        border: '1px solid #E8ECF0',
        background: '#F8FAFB',
        transition: 'all 0.15s',
    };

    const benefitLabelStyle = {
        fontSize: 11,
        fontWeight: 600,
        color: '#6B7280',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        marginBottom: 6,
    };

    const benefitValueStyle = {
        fontSize: 22,
        fontWeight: 800,
        color: '#1A1D21',
        letterSpacing: '-0.3px',
        lineHeight: 1,
    };

    const benefitSubStyle = {
        fontSize: 11,
        color: '#9CA3AF',
        marginTop: 4,
    };

    /* ── Cost-sharing helpers ─────────────────────────────────────────
     * Compute year-to-date amounts met for deductible / OOP. In a real
     * portal these come back from the eligibility API — here we derive
     * them deterministically from the member id so the demo numbers are
     * stable across renders but vary per member.
     */
    const computeMet = (limit, seed) => {
        if (!limit) return 0;
        // Simple deterministic hash → 10–80% of the limit
        const pct = ((seed * 37) % 71 + 10) / 100;
        return Math.round(limit * pct);
    };

    const formatCurrency = (n) =>
        `$${(n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

    /* ── Plan-document download (mock) ────────────────────────────────
     * In production each plan would have signed download URLs to its
     * SBC, EOC, and formulary PDFs. The demo simulates the click with a
     * Toast so the wiring is visible without serving real PDFs.
     */
    const handleDocumentDownload = (label) => {
        if (toast?.info) {
            toast.info(`${label} would download here`, { duration: 2500 });
        } else {
            // Fallback if Toast provider isn't mounted
            // eslint-disable-next-line no-alert
            alert(`${label} would download here.`);
        }
    };

    /* ── Progress bar — used in the Cost Sharing tiles ────────────── */
    const ProgressBar = ({ value, max, color }) => {
        const pct = max ? Math.min(100, Math.round((value / max) * 100)) : 0;
        return (
            <div style={{
                width: '100%', height: 6, borderRadius: 3,
                background: '#E5E7EB', overflow: 'hidden', marginTop: 10,
            }}>
                <div style={{
                    width: `${pct}%`, height: '100%',
                    background: `linear-gradient(90deg, ${color}, ${color}CC)`,
                    transition: 'width 0.3s',
                }} />
            </div>
        );
    };

    return (
        <div>
            {/* Page Header */}
            <div className="page-header">
                <div>
                    <h1>Eligibility & Benefits</h1>
                    <p>Submit eligibility request and receive the result in real-time</p>
                </div>
            </div>

            {/* Search Form */}
            <div className="card">
                <h3>Member Search</h3>
                <div className="form-grid">
                    <div className="form-group form-full">
                        <label>Group ID</label>
                        <select value={groupId} onChange={e => setGroupId(e.target.value)}>
                            
                            <option value="">All Groups</option>
                            {groups.map(group => (
                                <option key={group.Group_Id} value={group.Group_Id}>{group.Group_Id}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group form-full">
                        <label>Search By</label>
                        <select
                            value={searchBy}
                            onChange={(e) => { setSearchBy(e.target.value); handleClear(); setResultStatus(false); }}
                        >
                            <option value="memberId">Member ID</option>
                            <option value="name">First Name, Last Name & DOB</option>
                        </select>
                    </div>

                    {searchBy === 'memberId' ? (
                        <div className="form-group form-full">
                            <label className="required">Member ID</label>
                            <input
                                type="text"
                                placeholder="e.g. SND-100234"
                                value={memberId}
                                onChange={(e) => setMemberId(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                    ) : (
                        <>
                            <div className="form-group">
                                <label className="required">First Name</label>
                                <input
                                    type="text"
                                    placeholder="Enter first name"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                />
                            </div>
                            <div className="form-group">
                                <label className="required">Last Name</label>
                                <input
                                    type="text"
                                    placeholder="Enter last name"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                />
                            </div>
                            <div className="form-group form-full">
                                <label className="required">Date of Birth</label>
                                <input
                                    type="date"
                                        value={dob}
                                        placeholder="Select DOB"
                                    onChange={(e) => setDob(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                />
                            </div>
                        </>
                    )}

                    <div
                        className="form-full"
                        style={{ display: 'flex', gap: 12, marginTop: 4 }}
                    >
                        <button
                            className="btn-primary"
                            onClick={handleSearch}
                            disabled={loading}
                            aria-busy={loading}
                        >
                            {loading ? 'Searching…' : 'Search'}
                        </button>
                        <button className="btn-secondary" onClick={handleClear} disabled={loading}>
                            Clear
                        </button>
                    </div>
                </div>
            </div>

            {/* Loading & Error States */}
            {loading && (
                <div className="card">
                    <LoadingSpinner message="Searching members..." />
                </div>
            )}

            {error && (
                <ErrorCard message={error} onRetry={handleSearch} />
            )}

            {/* Search Results */}
            {searched && !loading && !error && !resultStatus && (
                <div
                    className="card"
                    style={{ textAlign: 'center', padding: '48px 24px' }}
                >
                    <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.5 }}>
                        <Search size={36} color="#9CA3AF" strokeWidth={1.5} />
                    </div>
                    <div
                        style={{
                            fontSize: 18,
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            marginBottom: 4,
                        }}
                    >
                        No Member Found
                    </div>
                    <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
                        Try searching with a different ID, name, or date of birth.
                    </div>
                </div>
            )}


            {!loading && !error && resultStatus && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* ─── Hero Banner ─── */}
                    <div style={{
                        background: 'linear-gradient(135deg, #0F766E, #134E4A)',
                        borderRadius: 16, padding: '24px 28px',
                        color: '#fff', position: 'relative', overflow: 'hidden',
                        boxShadow: '0 4px 20px rgba(13,148,136,0.15)',
                    }}>
                    <div style={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                        <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                            <div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Member Found</div>
                                <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.5px' }}>{result?.firstName} {result?.lastName}</div>
                                <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.85)', marginTop: 4 }}>
                                    {result?.Member?.MemberId} · {result?.Member?.PlanDescription}
                                </div>
                            </div>
                            <span className={`status-badge ${statusClass(result?.Member?.Status)}`} style={{ fontSize: 13, padding: '6px 14px', background: result?.Member?.status === 'Active' ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)', color: result?.Member?.status === 'Active' ? '#34D399' : '#FCA5A5' }}>
                                {result?.Member?.Status}
                            </span>
                        </div>
                    </div>

                    {/* ─── Section: Member Information ─── */}
                    <div className="card" style={{ marginBottom: 0 }}>
                        <div style={sectionHeaderStyle}>
                            <div style={sectionIconStyle('#0D9488')}>
                                <User size={16} color="#fff" strokeWidth={2.5} />
                            </div>
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1A1D21' }}>Member Information</h3>
                        </div>
                        <div className="detail-grid">
                            <div className="detail-row">
                                <span className="detail-label">Full Name</span>
                                <span className="detail-value">{result?.Member?.SubFirstName} {result?.Member?.SubLastName}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Member ID</span>
                                <span className="detail-value">{result?.Member?.MemberId}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Date of Birth</span>
                                <span className="detail-value">{formatDate(result?.Member?.SubDob)}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Gender</span>
                                {/*<span className="detail-value">{result?.Member?.Sex?.toLowerCase()==="m"?"Male":"Female"}</span>*/}
                                <span className="detail-value">
                                    {
                                        result?.Member?.Sex?.toLowerCase() === "male"
                                            ? "Male"
                                            : result?.Member?.Sex?.toLowerCase() === "female"
                                                ? "Female"
                                                : ""
                                    }
                                </span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Plan</span>
                                {/*<span className="detail-value">{result?.Member?.PlanDescription}</span>*/}
                                <span className="detail-value">
                                    {result?.Member?.PlanDescription?.trim() || result?.Member?.PlanId}
                                </span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Group #</span>
                                <span className="detail-value">{result?.Member?.GroupId}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Subscriber ID</span>
                                {/*<span className="detail-value">{result?.Member?.MemberId || 'N/A'}</span>*/}
                                <span className="detail-value">
                                    {result?.Member?.MemberId?.split('-')[0] || '-'}
                                </span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Status</span>
                                <span className="detail-value">
                                    {/*<span className={`status-badge ${statusClass(result?.Member?.Status)}`}>{result?.Member?.Status}</span>*/}
                                    {result?.Member?.Status && (
                                        <span className={`status-badge ${statusClass(result?.Member?.Status)}`}>
                                            {result?.Member?.Status}
                                        </span>
                                    )}
                                </span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Rider D </span>
                                
                                <span className="detail-value">
                                    {result?.Member?.FederalId || '-'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* ─── Section: Member Address ─── */}
                    <div className="card" style={{ marginBottom: 0 }}>
                        <div style={sectionHeaderStyle}>
                            <div style={sectionIconStyle('#0D9488')}>
                                <MapPinned size={16} color="#fff" strokeWidth={2.5} />
                            </div>
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1A1D21' }}>Member Address</h3>
                        </div>
                        <div className="detail-grid">
                            <div className="detail-row">
                                <span className="detail-label">Address </span>
                                <span className="detail-value">{result?.Member?.Address || "-"}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Address2</span>
                                <span className="detail-value">{result?.Member?.Address2 || "-"}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">City</span>
                                <span className="detail-value">{result?.Member?.City || "-"}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">State</span>
                                
                                <span className="detail-value">
                                    {result?.Member?.State || "-"} 
                                </span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Zip</span>
                                {/*<span className="detail-value">{result?.Member?.PlanDescription}</span>*/}
                                <span className="detail-value">
                                    {result?.Member?.Zip || "-" } 
                                </span>
                            </div>
                            
                        </div>
                    </div>

                    {/* ─── Section: Eligibility Dates ─── */}
                    <div className="card" style={{ marginBottom: 0 }}>
                        <div style={sectionHeaderStyle}>
                            <div style={sectionIconStyle('#3B82F6')}>
                                <Calendar size={16} color="#fff" strokeWidth={2.5} />
                            </div>
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1A1D21' }}>Current Eligibility Period</h3>
                        </div>
                        <div className="detail-grid">
                            <div className="detail-row">
                                <span className="detail-label">Plan Id </span>
                                {/*<span className="detail-value" style={{ color: '#0D9488', fontWeight: 700 }}>{formatDate(result?.Member?.PlanStart)}</span>*/}
                                <span className="detail-value" style={{ color: '#0D9488', fontWeight: 700 }}>{result?.Member?.PlanId || '-'}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Plan name </span>
                                
                                <span className="detail-value" style={{ color: '#0D9488', fontWeight: 700 }}>{result?.Member?.PlanDescription || '-'}</span>
                            </div>
                        </div>
                        <div className="detail-grid mt-3">
                            <div className="detail-row ">
                                <span className="detail-label">Start Date</span>
                                {/*<span className="detail-value" style={{ color: '#0D9488', fontWeight: 700 }}>{formatDate(result?.Member?.PlanStart)}</span>*/}
                                <span className="detail-value" style={{ color: '#0D9488', fontWeight: 700 }}>{formatDate(result?.Member?.PlanStart)}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">End Date</span>
                                {/*<span className="detail-value" style={{ color: result.status === 'Terminated' ? '#EF4444' : '#0D9488', fontWeight: 700 }}>{formatDate(result?.Member?.PlanEnd)}</span>*/}
                                <span className="detail-value" style={{ color: '#0D9488', fontWeight: 700 }}>{formatDate(result?.Member?.PlanEnd)}</span>
                            </div>
                        </div>
                    </div>

                    {/* ─── Section: Medical Benefits ─── */}
                    {result?.Copays && result?.Copays?.length > 0 && (
                        <div className="card" style={{ marginBottom: 0 }}>
                            <div style={sectionHeaderStyle}>
                                <div style={sectionIconStyle('#10B981')}>
                                    <Stethoscope size={16} color="#fff" strokeWidth={2.5} />
                                </div>
                                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1A1D21' }}>
                                    Medical Benefits
                                </h3>
                            </div>

                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                                    gap: 12
                                }}
                            >
                                {result?.Copays?.map((item, index) => (
                                    <div key={index} style={benefitTileStyle}>
                                        <div style={benefitLabelStyle}>
                                            {cleanLabel(item.BenefitDesc)}
                                        </div>

                                        <div style={benefitValueStyle}>
                                            {cleanLabel(item.CopayAmtNet)}
                                        </div>

                                        <div style={benefitSubStyle}>
                                            per visit
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {/*<div className="card" style={{ marginBottom: 0 }}>*/}
                    {/*    <div style={sectionHeaderStyle}>*/}
                    {/*        <div style={sectionIconStyle('#10B981')}>*/}
                    {/*            <Stethoscope size={16} color="#fff" strokeWidth={2.5} />*/}
                    {/*        </div>*/}
                    {/*        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1A1D21' }}>Medical Benefits</h3>*/}
                    {/*    </div>*/}
                    {/*    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>*/}
                    {/*        <div style={benefitTileStyle}>*/}
                    {/*            <div style={benefitLabelStyle}>Primary Care Copay</div>*/}
                    {/*            <div style={benefitValueStyle}>${(result.medicalCopay ?? result.copay ?? 0).toFixed(2)}</div>*/}
                    {/*            <div style={benefitSubStyle}>per visit</div>*/}
                    {/*        </div>*/}
                    {/*        <div style={benefitTileStyle}>*/}
                    {/*            <div style={benefitLabelStyle}>Specialist Copay</div>*/}
                    {/*            <div style={benefitValueStyle}>${(result.specialistCopay ?? 0).toFixed(2)}</div>*/}
                    {/*            <div style={benefitSubStyle}>per visit</div>*/}
                    {/*        </div>*/}
                    {/*        <div style={benefitTileStyle}>*/}
                    {/*            <div style={benefitLabelStyle}>Urgent Care</div>*/}
                    {/*            <div style={benefitValueStyle}>${((result.medicalCopay ?? 35) + 25).toFixed(2)}</div>*/}
                    {/*            <div style={benefitSubStyle}>per visit</div>*/}
                    {/*        </div>*/}
                    {/*        <div style={benefitTileStyle}>*/}
                    {/*            <div style={benefitLabelStyle}>Emergency Room</div>*/}
                    {/*            <div style={benefitValueStyle}>$350</div>*/}
                    {/*            <div style={benefitSubStyle}>waived if admitted</div>*/}
                    {/*        </div>*/}
                    {/*    </div>*/}
                    {/*</div>*/}

                    {/* ─── Section: Cost Sharing & Limits (Deductible + OOP) ─── */}
                    {/*<div className="card" style={{ marginBottom: 0 }}>*/}
                    {/*    <div style={sectionHeaderStyle}>*/}
                    {/*        <div style={sectionIconStyle('#F97316')}>*/}
                    {/*            <Wallet size={16} color="#fff" strokeWidth={2.5} />*/}
                    {/*        </div>*/}
                    {/*        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1A1D21' }}>Cost Sharing &amp; Limits</h3>*/}
                    {/*        <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>*/}
                    {/*            Plan Year {new Date(result?.Member?.PlanStart).getFullYear()}*/}
                    {/*        </span>*/}
                    {/*    </div>*/}

                    {/*    */}{/* Deductible row — Individual + Family */}
                    {/*    <div style={{ marginBottom: 8, fontSize: 13, fontWeight: 700, color: '#1A1D21' }}>Deductible</div>*/}
                    {/*    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 18 }}>*/}
                    {/*        {(() => {*/}
                    {/*            const indMax = result.deductible || 0;*/}
                    {/*            const indMet = result.deductibleMet ?? computeMet(indMax, result.id || 1);*/}
                    {/*            const indRemaining = Math.max(0, indMax - indMet);*/}
                    {/*            return (*/}
                    {/*                <div style={{ ...benefitTileStyle, borderColor: 'rgba(249,115,22,0.18)', background: 'rgba(249,115,22,0.04)' }}>*/}
                    {/*                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>*/}
                    {/*                        <div style={benefitLabelStyle}>Individual</div>*/}
                    {/*                        <div style={{ fontSize: 11, fontWeight: 600, color: '#F97316' }}>{indMax > 0 ? Math.round((indMet / indMax) * 100) : 0}% met</div>*/}
                    {/*                    </div>*/}
                    {/*                    <div style={benefitValueStyle}>{formatCurrency(indMax)}</div>*/}
                    {/*                    <div style={benefitSubStyle}>*/}
                    {/*                        Met {formatCurrency(indMet)} · Remaining <span style={{ color: '#1A1D21', fontWeight: 700 }}>{formatCurrency(indRemaining)}</span>*/}
                    {/*                    </div>*/}
                    {/*                    <ProgressBar value={indMet} max={indMax} color="#F97316" />*/}
                    {/*                </div>*/}
                    {/*            );*/}
                    {/*        })()}*/}
                    {/*        {(() => {*/}
                    {/*            const famMax = (result.deductibleFamily ?? (result.deductible || 0) * 2);*/}
                    {/*            const famMet = result.deductibleFamilyMet ?? computeMet(famMax, (result.id || 1) + 3);*/}
                    {/*            const famRemaining = Math.max(0, famMax - famMet);*/}
                    {/*            return (*/}
                    {/*                <div style={{ ...benefitTileStyle, borderColor: 'rgba(249,115,22,0.18)', background: 'rgba(249,115,22,0.04)' }}>*/}
                    {/*                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>*/}
                    {/*                        <div style={benefitLabelStyle}>Family</div>*/}
                    {/*                        <div style={{ fontSize: 11, fontWeight: 600, color: '#F97316' }}>{famMax > 0 ? Math.round((famMet / famMax) * 100) : 0}% met</div>*/}
                    {/*                    </div>*/}
                    {/*                    <div style={benefitValueStyle}>{formatCurrency(famMax)}</div>*/}
                    {/*                    <div style={benefitSubStyle}>*/}
                    {/*                        Met {formatCurrency(famMet)} · Remaining <span style={{ color: '#1A1D21', fontWeight: 700 }}>{formatCurrency(famRemaining)}</span>*/}
                    {/*                    </div>*/}
                    {/*                    <ProgressBar value={famMet} max={famMax} color="#F97316" />*/}
                    {/*                </div>*/}
                    {/*            );*/}
                    {/*        })()}*/}
                    {/*    </div>*/}

                    {/*    */}{/* Out-of-Pocket row — Individual + Family */}
                    {/*    <div style={{ marginBottom: 8, fontSize: 13, fontWeight: 700, color: '#1A1D21' }}>Out-of-Pocket Maximum</div>*/}
                    {/*    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>*/}
                    {/*        {(() => {*/}
                    {/*            const indMax = result.outOfPocketMax || 0;*/}
                    {/*            const indMet = result.outOfPocketMet ?? computeMet(indMax, (result.id || 1) + 7);*/}
                    {/*            const indRemaining = Math.max(0, indMax - indMet);*/}
                    {/*            return (*/}
                    {/*                <div style={{ ...benefitTileStyle, borderColor: 'rgba(220,38,38,0.18)', background: 'rgba(220,38,38,0.04)' }}>*/}
                    {/*                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>*/}
                    {/*                        <div style={benefitLabelStyle}>Individual</div>*/}
                    {/*                        <div style={{ fontSize: 11, fontWeight: 600, color: '#DC2626' }}>{indMax > 0 ? Math.round((indMet / indMax) * 100) : 0}% met</div>*/}
                    {/*                    </div>*/}
                    {/*                    <div style={benefitValueStyle}>{formatCurrency(indMax)}</div>*/}
                    {/*                    <div style={benefitSubStyle}>*/}
                    {/*                        Met {formatCurrency(indMet)} · Remaining <span style={{ color: '#1A1D21', fontWeight: 700 }}>{formatCurrency(indRemaining)}</span>*/}
                    {/*                    </div>*/}
                    {/*                    <ProgressBar value={indMet} max={indMax} color="#DC2626" />*/}
                    {/*                </div>*/}
                    {/*            );*/}
                    {/*        })()}*/}
                    {/*        {(() => {*/}
                    {/*            const famMax = (result.outOfPocketFamilyMax ?? (result.outOfPocketMax || 0) * 2);*/}
                    {/*            const famMet = result.outOfPocketFamilyMet ?? computeMet(famMax, (result.id || 1) + 11);*/}
                    {/*            const famRemaining = Math.max(0, famMax - famMet);*/}
                    {/*            return (*/}
                    {/*                <div style={{ ...benefitTileStyle, borderColor: 'rgba(220,38,38,0.18)', background: 'rgba(220,38,38,0.04)' }}>*/}
                    {/*                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>*/}
                    {/*                        <div style={benefitLabelStyle}>Family</div>*/}
                    {/*                        <div style={{ fontSize: 11, fontWeight: 600, color: '#DC2626' }}>{famMax > 0 ? Math.round((famMet / famMax) * 100) : 0}% met</div>*/}
                    {/*                    </div>*/}
                    {/*                    <div style={benefitValueStyle}>{formatCurrency(famMax)}</div>*/}
                    {/*                    <div style={benefitSubStyle}>*/}
                    {/*                        Met {formatCurrency(famMet)} · Remaining <span style={{ color: '#1A1D21', fontWeight: 700 }}>{formatCurrency(famRemaining)}</span>*/}
                    {/*                    </div>*/}
                    {/*                    <ProgressBar value={famMet} max={famMax} color="#DC2626" />*/}
                    {/*                </div>*/}
                    {/*            );*/}
                    {/*        })()}*/}
                    {/*    </div>*/}
                    {/*</div>*/}

                    {/* ─── Section: Pharmacy (Rx) Benefits ─── */}
                    {/*{result?.RxCopays && result?.RxCopays?.length > 0 && (*/}
                    {/*    <div className="card" style={{ marginBottom: 0 }}>*/}
                    {/*        <div style={sectionHeaderStyle}>*/}
                    {/*            <div style={sectionIconStyle('#8B5CF6')}>*/}
                    {/*                <Pill size={16} color="#fff" strokeWidth={2.5} />*/}
                    {/*            </div>*/}
                    {/*            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1A1D21' }}>Pharmacy (Rx) Benefits</h3>*/}
                    {/*            <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>*/}
                    {/*                5-Tier Formulary*/}
                    {/*            </span>*/}
                    {/*        </div>*/}

                    {/*        */}{/* Retail (30-day supply) */}
                    {/*        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>*/}
                    {/*            <div style={{ fontSize: 13, fontWeight: 700, color: '#1A1D21' }}>Retail Pharmacy</div>*/}
                    {/*            <div style={{ fontSize: 11, fontWeight: 600, color: '#8B5CF6', background: 'rgba(139,92,246,0.08)', padding: '3px 10px', borderRadius: 999 }}>*/}
                    {/*                30-day supply*/}
                    {/*            </div>*/}
                    {/*        </div>*/}
                    {/*        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 18 }}>*/}
                    {/*            {(() => {*/}
                    {/*                const tier1 = result.rxGenericCopay ?? 0;                 // Preferred Generic*/}
                    {/*                const tier2 = result.rxNonPreferredGenericCopay ?? Math.round(tier1 * 2);  // Non-Preferred Generic*/}
                    {/*                const tier3 = result.rxBrandCopay ?? 0;                   // Preferred Brand*/}
                    {/*                const tier4 = result.rxNonPreferredBrandCopay ?? Math.round(tier3 * 2);    // Non-Preferred Brand*/}
                    {/*                const tier5 = result.rxSpecialtyCopay ?? 0;              // Specialty*/}
                    {/*                const tiers = [*/}
                    {/*                    { tier: 1, label: 'Preferred Generic', value: tier1, badge: 'Lowest cost', accent: '#10B981' },*/}
                    {/*                    { tier: 2, label: 'Non-Preferred Generic', value: tier2, badge: 'Generic', accent: '#22C55E' },*/}
                    {/*                    { tier: 3, label: 'Preferred Brand', value: tier3, badge: 'On formulary', accent: '#3B82F6' },*/}
                    {/*                    { tier: 4, label: 'Non-Preferred Brand', value: tier4, badge: 'Higher cost', accent: '#F59E0B' },*/}
                    {/*                    { tier: 5, label: 'Specialty', value: tier5, badge: 'PA may be required', accent: '#DC2626' },*/}
                    {/*                ];*/}
                    {/*                return tiers.map((t) => (*/}
                    {/*                    <div*/}
                    {/*                        key={`retail-${t.tier}`}*/}
                    {/*                        style={{*/}
                    {/*                            ...benefitTileStyle,*/}
                    {/*                            borderColor: `${t.accent}30`,*/}
                    {/*                            background: `linear-gradient(135deg, ${t.accent}05, rgba(139,92,246,0.02))`,*/}
                    {/*                            position: 'relative',*/}
                    {/*                        }}*/}
                    {/*                    >*/}
                    {/*                        <div style={{*/}
                    {/*                            position: 'absolute', top: 10, right: 10,*/}
                    {/*                            width: 22, height: 22, borderRadius: '50%',*/}
                    {/*                            background: `${t.accent}15`, color: t.accent,*/}
                    {/*                            display: 'flex', alignItems: 'center', justifyContent: 'center',*/}
                    {/*                            fontSize: 10, fontWeight: 800,*/}
                    {/*                        }}>*/}
                    {/*                            T{t.tier}*/}
                    {/*                        </div>*/}
                    {/*                        <div style={{ ...benefitLabelStyle, paddingRight: 28 }}>{t.label}</div>*/}
                    {/*                        <div style={benefitValueStyle}>${Number(t.value).toFixed(2)}</div>*/}
                    {/*                        <div style={{ ...benefitSubStyle, color: t.accent, fontWeight: 600 }}>{t.badge}</div>*/}
                    {/*                    </div>*/}
                    {/*                ));*/}
                    {/*            })()}*/}
                    {/*        </div>*/}

                    {/*        */}{/* Mail-Order (90-day supply) */}
                    {/*        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>*/}
                    {/*            <div style={{ fontSize: 13, fontWeight: 700, color: '#1A1D21' }}>Mail-Order Pharmacy</div>*/}
                    {/*            <div style={{ fontSize: 11, fontWeight: 600, color: '#0D9488', background: 'rgba(13,148,136,0.08)', padding: '3px 10px', borderRadius: 999 }}>*/}
                    {/*                90-day supply · Save with bulk*/}
                    {/*            </div>*/}
                    {/*        </div>*/}
                    {/*        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>*/}
                    {/*            {(() => {*/}
                    {/*                // Mail-order is typically 2.5× the retail price for a 90-day supply*/}
                    {/*                // (vs. 3× via retail) — that's the standard mail-order discount.*/}
                    {/*                const factor = 2.5;*/}
                    {/*                const tier1 = Math.round((result.rxGenericCopay ?? 0) * factor);*/}
                    {/*                const tier2 = Math.round((result.rxNonPreferredGenericCopay ?? (result.rxGenericCopay ?? 0) * 2) * factor);*/}
                    {/*                const tier3 = Math.round((result.rxBrandCopay ?? 0) * factor);*/}
                    {/*                const tier4 = Math.round((result.rxNonPreferredBrandCopay ?? (result.rxBrandCopay ?? 0) * 2) * factor);*/}
                    {/*                const tier5 = result.rxSpecialtyCopay ?? 0; // Specialty drugs typically NOT available mail-order*/}
                    {/*                const tiers = [*/}
                    {/*                    { tier: 1, label: 'Preferred Generic', value: tier1, accent: '#10B981', sub: '90-day' },*/}
                    {/*                    { tier: 2, label: 'Non-Preferred Generic', value: tier2, accent: '#22C55E', sub: '90-day' },*/}
                    {/*                    { tier: 3, label: 'Preferred Brand', value: tier3, accent: '#3B82F6', sub: '90-day' },*/}
                    {/*                    { tier: 4, label: 'Non-Preferred Brand', value: tier4, accent: '#F59E0B', sub: '90-day' },*/}
                    {/*                    { tier: 5, label: 'Specialty', value: tier5, accent: '#DC2626', sub: 'Retail only' },*/}
                    {/*                ];*/}
                    {/*                return tiers.map((t) => (*/}
                    {/*                    <div*/}
                    {/*                        key={`mail-${t.tier}`}*/}
                    {/*                        style={{*/}
                    {/*                            ...benefitTileStyle,*/}
                    {/*                            borderColor: `${t.accent}25`,*/}
                    {/*                            background: '#FAFBFC',*/}
                    {/*                            position: 'relative',*/}
                    {/*                            opacity: t.sub === 'Retail only' ? 0.6 : 1,*/}
                    {/*                        }}*/}
                    {/*                    >*/}
                    {/*                        <div style={{*/}
                    {/*                            position: 'absolute', top: 10, right: 10,*/}
                    {/*                            width: 22, height: 22, borderRadius: '50%',*/}
                    {/*                            background: `${t.accent}15`, color: t.accent,*/}
                    {/*                            display: 'flex', alignItems: 'center', justifyContent: 'center',*/}
                    {/*                            fontSize: 10, fontWeight: 800,*/}
                    {/*                        }}>*/}
                    {/*                            T{t.tier}*/}
                    {/*                        </div>*/}
                    {/*                        <div style={{ ...benefitLabelStyle, paddingRight: 28 }}>{t.label}</div>*/}
                    {/*                        <div style={benefitValueStyle}>{t.sub === 'Retail only' ? '—' : `$${Number(t.value).toFixed(2)}`}</div>*/}
                    {/*                        <div style={benefitSubStyle}>{t.sub}</div>*/}
                    {/*                    </div>*/}
                    {/*                ));*/}
                    {/*            })()}*/}
                    {/*        </div>*/}

                    {/*        */}{/* Footnote */}
                    {/*        <div style={{*/}
                    {/*            marginTop: 14, padding: '10px 14px',*/}
                    {/*            background: 'rgba(139,92,246,0.05)',*/}
                    {/*            border: '1px solid rgba(139,92,246,0.15)',*/}
                    {/*            borderRadius: 10, fontSize: 12, color: '#6B5BBF', lineHeight: 1.5,*/}
                    {/*        }}>*/}
                    {/*            <strong>Tier guide:</strong> Preferred drugs are on the plan formulary at the lowest cost.*/}
                    {/*            Non-preferred drugs are covered but at a higher copay. Specialty drugs may require prior authorization*/}
                    {/*            and are dispensed through designated specialty pharmacies. See the Drug Formulary in Plan Documents below for the full list.*/}
                    {/*        </div>*/}
                    {/*    </div>*/}
                    {/*)}*/}


                    {/* ─── Section: (Rx) Benefits ─── */}
                    
                        <div className="card" style={{ marginBottom: 0 }}>
                            <div style={sectionHeaderStyle}>
                                <div style={sectionIconStyle('#8B5CF6')}>
                                    <Pill size={16} color="#fff" strokeWidth={2.5} />
                                </div>
                                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1A1D21' }}>Rx Benefits</h3>
                                <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                        
                                </span>
                            </div>

                            
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 18 }}>
                                {(() => {
                                const tier1 = result?.Member?.PharmacyFormulary30 ?? 0; 
                                const tier2 = result?.Member?.PharmacyFormulary90 ?? 0;  
                                const tier3 = result?.Member?.PharmacyNonFormulary30 ?? 0; 
                                const tier4 = result?.Member?.PharmacyNonFormulary90 ?? 0;
                                    
                                    const tiers = [
                                        { tier: 1, label: 'Pharmacy Formulary (0-30 days supply)', value: tier1, badge: '', accent: '#10B981' },
                                        { tier: 2, label: 'Pharmacy Formulary (31-90 days supply)', value: tier2, badge: '', accent: '#22C55E' },
                                        { tier: 3, label: 'Pharmacy Non-Formulary (0-30 days supply)', value: tier3, badge: '', accent: '#3B82F6' },
                                        { tier: 4, label: 'Pharmacy Non-Formulary (31-90 days supply)', value: tier4, badge: '', accent: '#F59E0B' },
                                        
                                    ];
                                    return tiers.map((t) => (
                                        <div
                                            key={`retail-${t.tier}`}
                                            style={{
                                                ...benefitTileStyle,
                                                borderColor: `${t.accent}30`,
                                                background: `linear-gradient(135deg, ${t.accent}05, rgba(139,92,246,0.02))`,
                                                position: 'relative',
                                            }}
                                        >
                                            <div style={{
                                                position: 'absolute', top: 10, right: 10,
                                                width: 22, height: 22, borderRadius: '50%',
                                                background: `${t.accent}15`, color: t.accent,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: 10, fontWeight: 800,
                                            }}>
                                                T{t.tier}
                                            </div>
                                            <div style={{
                                                ...benefitLabelStyle,
                                                paddingRight: 28,
                                                fontWeight: 700, // or "bold"
                                              
                                            }}>{t.label}</div>
                                            <div style={{
                                                benefitValueStyle,
                                                 fontWeight: 700, // or "bold"
                                            
                                            }}>${Number(t.value).toFixed(2)}</div>
                                            <div style={{ ...benefitSubStyle, color: t.accent, fontWeight: 600 }}>{t.badge}</div>
                                        </div>
                                    ));
                                })()}
                            </div>


                            
                        </div>
                    

                    

                    {/* ─── Section: Plan Documents ─── */}
                    {/*<div className="card" style={{ marginBottom: 0 }}>*/}
                    {/*    <div style={sectionHeaderStyle}>*/}
                    {/*        <div style={sectionIconStyle('#6366F1')}>*/}
                    {/*            <FileText size={16} color="#fff" strokeWidth={2.5} />*/}
                    {/*        </div>*/}
                    {/*        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1A1D21' }}>Plan Documents</h3>*/}
                    {/*        <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: '#6B7280' }}>*/}
                    {/*            {result.planId || result.planName}*/}
                    {/*        </span>*/}
                    {/*    </div>*/}
                    {/*    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>*/}
                    {/*        {[*/}
                    {/*            {*/}
                    {/*                label: 'Summary of Benefits',*/}
                    {/*                description: 'A short, plain-language overview of what the plan covers, copays, and limits (SBC).',*/}
                    {/*                filename: `SBC_${(result.planId || 'plan').replace(/\s+/g, '_')}.pdf`,*/}
                    {/*                color: '#6366F1',*/}
                    {/*            },*/}
                    {/*            {*/}
                    {/*                label: 'Plan Benefit Document',*/}
                    {/*                description: 'The full Evidence of Coverage (EOC) with detailed benefit terms, exclusions, and appeal rights.',*/}
                    {/*                filename: `EOC_${(result.planId || 'plan').replace(/\s+/g, '_')}.pdf`,*/}
                    {/*                color: '#0D9488',*/}
                    {/*            },*/}
                    {/*            {*/}
                    {/*                label: 'Drug Formulary',*/}
                    {/*                description: 'List of covered prescription drugs, tier placement, and any prior-authorization requirements.',*/}
                    {/*                filename: `Formulary_${(result.planId || 'plan').replace(/\s+/g, '_')}.pdf`,*/}
                    {/*                color: '#8B5CF6',*/}
                    {/*            },*/}
                    {/*        ].map((doc) => (*/}
                    {/*            <div*/}
                    {/*                key={doc.label}*/}
                    {/*                style={{*/}
                    {/*                    padding: 16, borderRadius: 12,*/}
                    {/*                    border: `1px solid ${doc.color}25`,*/}
                    {/*                    background: `linear-gradient(135deg, ${doc.color}06, transparent)`,*/}
                    {/*                    display: 'flex', flexDirection: 'column', gap: 10,*/}
                    {/*                    transition: 'all 0.15s',*/}
                    {/*                }}*/}
                    {/*                onMouseEnter={(e) => {*/}
                    {/*                    e.currentTarget.style.transform = 'translateY(-2px)';*/}
                    {/*                    e.currentTarget.style.boxShadow = `0 6px 16px ${doc.color}1A`;*/}
                    {/*                }}*/}
                    {/*                onMouseLeave={(e) => {*/}
                    {/*                    e.currentTarget.style.transform = 'translateY(0)';*/}
                    {/*                    e.currentTarget.style.boxShadow = 'none';*/}
                    {/*                }}*/}
                    {/*            >*/}
                    {/*                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>*/}
                    {/*                    <div style={{*/}
                    {/*                        width: 36, height: 36, borderRadius: 10,*/}
                    {/*                        background: `${doc.color}15`,*/}
                    {/*                        display: 'flex', alignItems: 'center', justifyContent: 'center',*/}
                    {/*                        flexShrink: 0,*/}
                    {/*                    }}>*/}
                    {/*                        <FileText size={18} color={doc.color} strokeWidth={2} />*/}
                    {/*                    </div>*/}
                    {/*                    <div style={{ flex: 1, minWidth: 0 }}>*/}
                    {/*                        <div style={{ fontSize: 14, fontWeight: 700, color: '#1A1D21' }}>{doc.label}</div>*/}
                    {/*                        <div style={{ fontSize: 11, color: '#9CA3AF', fontFamily: 'monospace', marginTop: 2, wordBreak: 'break-all' }}>*/}
                    {/*                            {doc.filename}*/}
                    {/*                        </div>*/}
                    {/*                    </div>*/}
                    {/*                </div>*/}
                    {/*                <p style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.5, margin: 0, flex: 1 }}>*/}
                    {/*                    {doc.description}*/}
                    {/*                </p>*/}
                    {/*                <div style={{ display: 'flex', gap: 8 }}>*/}
                    {/*                    <button*/}
                    {/*                        onClick={() => handleDocumentDownload(doc.label)}*/}
                    {/*                        style={{*/}
                    {/*                            flex: 1, padding: '8px 12px',*/}
                    {/*                            background: doc.color, color: '#fff',*/}
                    {/*                            border: 'none', borderRadius: 8,*/}
                    {/*                            fontSize: 12, fontWeight: 700, cursor: 'pointer',*/}
                    {/*                            fontFamily: 'inherit', display: 'inline-flex',*/}
                    {/*                            alignItems: 'center', justifyContent: 'center', gap: 6,*/}
                    {/*                        }}*/}
                    {/*                    >*/}
                    {/*                        <Download size={13} strokeWidth={2.5} /> Download PDF*/}
                    {/*                    </button>*/}
                    {/*                    <button*/}
                    {/*                        onClick={() => handleDocumentDownload(`${doc.label} (open)`)}*/}
                    {/*                        title={`Open ${doc.label} in a new tab`}*/}
                    {/*                        style={{*/}
                    {/*                            padding: '8px 10px',*/}
                    {/*                            background: '#fff', color: doc.color,*/}
                    {/*                            border: `1px solid ${doc.color}33`, borderRadius: 8,*/}
                    {/*                            fontSize: 12, fontWeight: 700, cursor: 'pointer',*/}
                    {/*                            fontFamily: 'inherit', display: 'inline-flex',*/}
                    {/*                            alignItems: 'center', justifyContent: 'center',*/}
                    {/*                        }}*/}
                    {/*                    >*/}
                    {/*                        <ExternalLink size={13} strokeWidth={2.5} />*/}
                    {/*                    </button>*/}
                    {/*                </div>*/}
                    {/*            </div>*/}
                    {/*        ))}*/}
                    {/*    </div>*/}
                    {/*</div>*/}

                    {/* ─── Section: Primary Care Physician ─── */}
                    {/*<div className="card" style={{ marginBottom: 0 }}>*/}
                    {/*    <div style={sectionHeaderStyle}>*/}
                    {/*        <div style={sectionIconStyle('#F59E0B')}>*/}
                    {/*            <UserCheck size={16} color="#fff" strokeWidth={2.5} />*/}
                    {/*        </div>*/}
                    {/*        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1A1D21' }}>Primary Care Physician</h3>*/}
                    {/*    </div>*/}
                    {/*    {(() => {*/}
                    {/*        const dir = PCP_DIRECTORY[result?.Member?.PcpFirstName+" "+result?.Member?.PcpFirstName] || {};*/}
                    {/*        const practice = result.pcpPractice || dir.practice || 'NA';*/}
                    {/*        const address = result?.Member?.PcpAddress + "," + result?.Member?.PcpAddress2 + "," + result?.Member?.PcpAddress2 + "," + result?.Member?.PcpCity || dir.address || 'Address on file with provider';*/}
                    {/*        return (*/}
                    {/*            <div className="detail-grid">*/}
                    {/*                <div className="detail-row">*/}
                    {/*                    <span className="detail-label">PCP Name</span>*/}
                    {/*                    <span className="detail-value">{result?.Member?.PcpFirstName} {result?.Member?.PcpLastName}</span>*/}
                    {/*                </div>*/}
                    {/*                <div className="detail-row">*/}
                    {/*                    <span className="detail-label">Practice</span>*/}
                    {/*                    <span className="detail-value">{practice}</span>*/}
                    {/*                </div>*/}
                    {/*                <div className="detail-row">*/}
                    {/*                    <span className="detail-label">PCP Phone</span>*/}
                    {/*                    <span className="detail-value">*/}
                    {/*                        <a href={`tel:${(result?.Member?.PcpPhone || '').replace(/[^0-9+]/g, '')}`} style={{ color: '#0D9488', fontWeight: 600, textDecoration: 'none' }}>*/}
                    {/*                            {result?.Member?.PcpPhone}*/}
                    {/*                        </a>*/}
                    {/*                    </span>*/}
                    {/*                </div>*/}
                    {/*                <div className="detail-row" style={{ gridColumn: '1 / -1' }}>*/}
                    {/*                    <span className="detail-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>*/}
                    {/*                        <MapPin size={13} color="#F59E0B" strokeWidth={2.5} /> Address*/}
                    {/*                    </span>*/}
                    {/*                    <span className="detail-value">*/}
                    {/*                        <a*/}
                    {/*                            href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}*/}
                    {/*                            target="_blank"*/}
                    {/*                            rel="noopener noreferrer"*/}
                    {/*                            style={{ color: '#0D9488', textDecoration: 'none', fontWeight: 600 }}*/}
                    {/*                        >*/}
                    {/*                            {address}*/}
                    {/*                        </a>*/}
                    {/*                    </span>*/}
                    {/*                </div>*/}
                    {/*            </div>*/}
                    {/*        );*/}
                    {/*    })()}*/}
                    {/*</div>*/}

                    {/* ─── Section: Eligibility History ─── */}
                    {result?.History && result?.History?.length > 0 && (
                        <div className="card" style={{ marginBottom: 0 }}>
                            <div style={sectionHeaderStyle}>
                                <div style={sectionIconStyle('#6366F1')}>
                                    <History size={16} color="#fff" strokeWidth={2.5} />
                                </div>
                                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1A1D21' }}>Eligibility History</h3>
                            </div>
                            <div style={{ position: 'relative', paddingLeft: 24 }}>
                                {/* Vertical timeline line */}
                                <div style={{ position: 'absolute', left: 9, top: 10, bottom: 10, width: 2, background: 'linear-gradient(180deg, #0D9488, #E5E7EB)' }} />
                                {result?.History?.map((h, i) => {
                                    const statusColors = {
                                        Active: { dot: '#10B981', badge: 'active', bg: 'rgba(16,185,129,0.06)', border: 'rgba(16,185,129,0.2)' },
                                        Terminated: { dot: '#EF4444', badge: 'terminated', bg: 'rgba(239,68,68,0.04)', border: 'rgba(239,68,68,0.15)' },
                                        Expired: { dot: '#9CA3AF', badge: 'info', bg: '#F8FAFB', border: '#E8ECF0' },
                                    };
                                    const sc = statusColors[h.status] || statusColors.Expired;
                                    return (
                                        <div key={i} style={{ position: 'relative', paddingBottom: i < result?.History?.length - 1 ? 16 : 0 }}>
                                            {/* Dot */}
                                            <div style={{
                                                position: 'absolute', left: -20, top: 14,
                                                width: 16, height: 16, borderRadius: '50%',
                                                background: sc.dot, border: '3px solid #fff',
                                                boxShadow: `0 0 0 2px ${sc.dot}`,
                                            }} />
                                            {/* Card */}
                                            <div style={{
                                                padding: '14px 16px', borderRadius: 12,
                                                background: sc.bg, border: `1px solid ${sc.border}`,
                                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                flexWrap: 'wrap', gap: 8,
                                            }}>
                                                <div>
                                                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1A1D21', marginBottom: 2 }}>
                                                        {h.PlanId}
                                                    </div>
                                                    <div style={{ fontSize: 12, color: '#6B7280' }}>
                                                        {formatDate(h.PlanStart)} → {formatDate(h.PlanEnd)}
                                                    </div>
                                                </div>
                                                {/*<span className={`status-badge ${sc.badge}`} style={{ fontSize: 11, padding: '3px 10px' }}>*/}
                                                {/*    {h.status}*/}
                                                {/*</span>*/}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Info Notice */}
            {/*<div*/}
            {/*    style={{*/}
            {/*        background: '#FEF3C7',*/}
            {/*        border: '1px solid #FDE68A',*/}
            {/*        borderRadius: 'var(--radius-md)',*/}
            {/*        padding: '14px 20px',*/}
            {/*        fontSize: 13,*/}
            {/*        color: '#92400E',*/}
            {/*        lineHeight: 1.6,*/}
            {/*        marginTop: 8,*/}
            {/*        display: 'flex',*/}
            {/*        alignItems: 'flex-start',*/}
            {/*        gap: 10,*/}
            {/*    }}*/}
            {/*>*/}
            {/*    */}{/*<span style={{ fontSize: 16, flexShrink: 0, lineHeight: 1.4 }}>*/}
            {/*    */}{/*    <AlertTriangle size={16} color="#92400E" strokeWidth={2} />*/}
            {/*    */}{/*</span>*/}
            {/*    */}{/*<span>*/}
            {/*    */}{/*    For terminated members, eligibility must be verified via the ACHP*/}
            {/*    */}{/*    Provider Portal.*/}
            {/*    */}{/*</span>*/}
            {/*</div>*/}
        </div>
    );
};

export default MemberSearchPage;
